/**
 * The glossary hub: every term with its one-line standfirst.
 *
 * Every term is linked by name from here, which is what makes the leaves
 * crawlable from something other than the sitemap. The standfirst rather than
 * the full definition, because a hub that reprints its leaves is a hub that
 * competes with them for the same query.
 */

import type { ContentLang } from "@fazly/shared/contentLang";
import { Link } from "@tanstack/react-router";

import { GLOSSARY_SLUGS, glossaryPath } from "../../lib/glossary/index";
import type { GlossaryBundle } from "../../lib/glossary/types";
import type { GlossaryUi } from "../../lib/glossary/ui";
import { ContentFooter } from "../ContentFooter";

export function GlossaryIndex({
  lang,
  ui,
  bundle,
}: {
  lang: ContentLang;
  ui: GlossaryUi;
  bundle: GlossaryBundle;
}) {
  // Alphabetical by the term as it reads in THIS language, not by slug: the
  // slugs are English and a Turkish reader scanning for "Yüz simetrisi" should
  // find it under Y.
  const ordered = [...GLOSSARY_SLUGS].sort((a, b) =>
    bundle[a].term.localeCompare(bundle[b].term, lang),
  );

  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-12 sm:py-16">
      <h1 className="font-display text-[clamp(2rem,7vw,3rem)] leading-[1.1] font-bold tracking-tight text-ink">
        {ui.indexH1}
      </h1>
      <p className="mt-4 max-w-xl text-lg leading-relaxed text-ink2">
        {ui.indexStandfirst}
      </p>

      <ul className="mt-10 divide-y divide-hair border-y-[2.5px] border-ink">
        {ordered.map((slug) => (
          <li key={slug}>
            <Link
              to={glossaryPath(slug, lang)}
              className="group flex flex-col gap-1 py-4 transition-colors hover:bg-inset"
            >
              <span className="font-display text-lg font-bold text-ink group-hover:text-brand">
                {bundle[slug].term}
              </span>
              <span className="text-[15px] leading-relaxed text-ink2">
                {bundle[slug].standfirst}
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <ContentFooter lang={lang} />
    </main>
  );
}
